/* ObiHub OS — kontrollü ana modül dock'u. Router çağırmaz; yalnız navigation intent'i callback ile bildirir. */
import React, { useEffect, useId, useRef, useState } from "react";
import "./shell.css";

const MAX_COMPACT_PRIMARY = 4;
const ITEM_ID = /^[a-z][a-z0-9.-]{1,31}$/;

function validateItems(items) {
  if (!Array.isArray(items) || items.length === 0 || items.length > 12) throw new Error("OsDock: 1–12 öğe zorunludur");
  const seen = new Set();
  for (const item of items) {
    if (!item || typeof item !== "object" || !ITEM_ID.test(item.id) || seen.has(item.id)) throw new Error("OsDock: öğe kimliği geçersiz veya yinelenmiş");
    if (typeof item.label !== "string" || item.label.trim() === "" || item.label.length > 24) throw new Error("OsDock: kısa ve erişilebilir etiket zorunludur");
    if (typeof item.path !== "string" || !item.path.startsWith("/") || item.path.startsWith("//") || /[?#%\\\u0000-\u001f\u007f]|:\/\/|\.\./.test(item.path)) throw new Error("OsDock: yalnız güvenli uygulama yolu kabul edilir");
    seen.add(item.id);
  }
  return items;
}

function DockItem({ item, active, onNavigate, compact = false }) {
  return (
    <button
      type="button"
      className="os-dock__item"
      data-active={active ? "true" : "false"}
      aria-current={active ? "page" : undefined}
      aria-label={item.label}
      onClick={() => onNavigate({ id: item.id, path: item.path })}
    >
      <span className="os-dock__icon" aria-hidden="true">{item.icon}</span>
      <span className="os-dock__label">{item.label}</span>
      {active ? <span className="os-visually-hidden">Etkin modül</span> : null}
      {compact ? <span className="os-visually-hidden">Alt menü öğesi</span> : null}
    </button>
  );
}

function MoreMenu({ items, activeId, onNavigate }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const first = menuRef.current?.querySelector('[role="menuitem"]');
    first?.focus();
    const outside = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);

  if (items.length === 0) return null;
  const keyDown = (event) => {
    const nodes = [...menuRef.current.querySelectorAll('[role="menuitem"]')];
    const index = nodes.indexOf(event.currentTarget);
    const move = (next) => { event.preventDefault(); nodes[(next + nodes.length) % nodes.length].focus(); };
    if (event.key === "ArrowDown") move(index + 1);
    else if (event.key === "ArrowUp") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(nodes.length - 1);
    else if (event.key === "Escape") { event.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
    else if (event.key === "Tab") setOpen(false);
  };
  return (
    <div className="os-dock__more">
      <button
        ref={triggerRef}
        type="button"
        className="os-dock__item"
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="os-dock__more-icon" aria-hidden="true">•••</span>
        <span className="os-dock__label">Daha fazla</span>
      </button>
      {open ? (
        <div ref={menuRef} id={menuId} role="menu" aria-label="Diğer modüller" className="os-dock__menu">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="os-dock__menuitem"
              aria-current={item.id === activeId ? "page" : undefined}
              onKeyDown={keyDown}
              onClick={() => { setOpen(false); onNavigate({ id: item.id, path: item.path }); }}
            >
              <span aria-hidden="true">{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function OsDock({ items, activeId = null, onNavigate, commandAction = null, hiddenForKeyboard = false }) {
  const safeItems = validateItems(items);
  if (typeof onNavigate !== "function") throw new Error("OsDock: onNavigate zorunludur");
  if (commandAction && (typeof commandAction !== "object" || typeof commandAction.label !== "string" || commandAction.label.trim() === "" || typeof commandAction.onInvoke !== "function")) throw new Error("OsDock: commandAction geçersiz");
  const primary = safeItems.slice(0, MAX_COMPACT_PRIMARY);
  const rest = safeItems.slice(MAX_COMPACT_PRIMARY);
  return (
    <nav className="os-dock" aria-label="Ana modüller" data-keyboard-hidden={hiddenForKeyboard ? "true" : "false"}>
      <div className="os-dock__desktop">
        {safeItems.map((item) => <DockItem key={item.id} item={item} active={item.id === activeId} onNavigate={onNavigate} />)}
        {commandAction ? <button type="button" className="os-dock__command" onClick={commandAction.onInvoke} aria-label={commandAction.label}>{commandAction.label}<kbd>⌘K</kbd></button> : null}
      </div>
      <div className="os-dock__compact">
        {primary.map((item) => <DockItem key={item.id} item={item} active={item.id === activeId} onNavigate={onNavigate} compact />)}
        <MoreMenu items={rest} activeId={activeId} onNavigate={onNavigate} />
      </div>
    </nav>
  );
}

export { validateItems as validateDockItems, MAX_COMPACT_PRIMARY };
